import React, { useState, useMemo } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { FORM_CONSTANTS } from '../constants';
import { ClearSectionDialog } from './ClearSectionDialog';
import { FormSectionFields } from './FormSectionFields';
import { FormSectionHeader } from './FormSectionHeader';
import { FormSectionProps } from './FormSection.types';

export const FormSection: React.FC<FormSectionProps> = React.memo(
  ({
    section,
    values,
    onFieldChange,
    getFieldError,
    compact = false,
    showTitle = true,
    preloadedOptions,
    isAdminMode = false,
    onSectionTitleChange,
    showCopyButton = false,
    onClearSection,
    sectionIndex,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [showClearConfirm, setShowClearConfirm] = useState(false);

    const handleClearSection = () => {
      if (onClearSection && sectionIndex !== undefined) {
        onClearSection(sectionIndex);
        setShowClearConfirm(false);
      }
    };

    const handleClearConfirm = () => {
      setShowClearConfirm(true);
    };

    const handleClearCancel = () => {
      setShowClearConfirm(false);
    };

    // Мемоизированные отступы для полей
    const fieldSpacing = useMemo(() => {
      if (isMobile) {
        return FORM_CONSTANTS.MOBILE_FIELD_SPACING.xs;
      }
      return compact ? FORM_CONSTANTS.FIELD_SPACING.xs : FORM_CONSTANTS.FIELD_SPACING.sm;
    }, [isMobile, compact]);

    return (
      <Box>
        {showTitle && (
          <FormSectionHeader
            section={section}
            isMobile={isMobile}
            isAdminMode={isAdminMode}
            onSectionTitleChange={onSectionTitleChange}
            onClear={onClearSection && sectionIndex !== undefined ? handleClearConfirm : undefined}
          />
        )}
        <FormSectionFields
          fields={section.fields}
          values={values}
          onFieldChange={onFieldChange}
          getFieldError={getFieldError}
          compact={compact}
          isMobile={isMobile}
          fieldSpacing={fieldSpacing}
          preloadedOptions={preloadedOptions}
          showCopyButton={showCopyButton}
        />
        <ClearSectionDialog
          title={section.title}
          isOpen={showClearConfirm}
          onCancel={handleClearCancel}
          onConfirm={handleClearSection}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Кастомная функция сравнения для оптимизации React.memo
    return (
      prevProps.section.title === nextProps.section.title &&
      JSON.stringify(prevProps.values) === JSON.stringify(nextProps.values) &&
      prevProps.compact === nextProps.compact &&
      prevProps.showTitle === nextProps.showTitle &&
      prevProps.isAdminMode === nextProps.isAdminMode &&
      prevProps.showCopyButton === nextProps.showCopyButton &&
      prevProps.sectionIndex === nextProps.sectionIndex &&
      prevProps.onClearSection === nextProps.onClearSection &&
      JSON.stringify(prevProps.preloadedOptions) === JSON.stringify(nextProps.preloadedOptions)
    );
  },
);
